import { CustomizedButton } from '@components/Customized';
import { useSnackbar } from '@components/Provider';
import {
  CapaErrorCode,
  useDeleteMyRefundAccountByQuotationUserMutation,
  useFetchMyQuotationUserQuery,
  useFetchMyRefundBankAccountsQuery,
  useUpdateIdentityVerificationMutation
} from '@generated/graphql';
import useIamport from '@hooks/useIamport';
import { Exclamation } from '../asset';
import RequestActionDialogBase, {
  RequestActionDialogProps
} from './based/RequestActionDialogBase';

const VerificationDialog = ({
  open,
  onClose,
  variant = 'default'
}: RequestActionDialogProps) => {
  const isTryingCreateRefundAccount = variant === 'account';
  const { enqueueSnackbar } = useSnackbar();
  const { certificate } = useIamport();
  const { data: myQuotationUser } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });
  const [updateIdentityVerification] = useUpdateIdentityVerificationMutation({
    refetchQueries: ['fetchMyQuotationUser']
  });
  const { data: myRefundBankAccounts } = useFetchMyRefundBankAccountsQuery();
  const refundBankAccountIds = (
    myRefundBankAccounts?.fetchMyRefundBankAccounts.data ?? []
  ).map((bankAccountsInfo) => bankAccountsInfo.id);

  const hasRequiredInfo =
    myQuotationUser?.fetchMyQuotationUser.data?.cellphoneNumber &&
    myQuotationUser?.fetchMyQuotationUser.data?.managerName;

  const [
    deleteMyRefundAccountByQuotationUser
  ] = useDeleteMyRefundAccountByQuotationUserMutation({
    refetchQueries: ['fetchMyRefundBankAccounts', 'fetchMyQuotationUser']
  });

  const handleVerificationClick = async () => {
    try {
      if (!hasRequiredInfo) {
        throw Error('필수정보 입력 후 본인 인증 해주세요.');
      }
      const certificateResponse = await certificate();

      if (!certificateResponse.success || !certificateResponse.imp_uid) {
        throw Error(certificateResponse.error_msg);
      }

      const result = await updateIdentityVerification({
        variables: {
          input: {
            impUid: certificateResponse.imp_uid
          }
        }
      });

      if (
        result.data?.updateIdentityVerification.result !== CapaErrorCode.Success
      ) {
        throw Error(result.data?.updateIdentityVerification.result);
      }

      refundBankAccountIds.forEach((refundBankAccountId) =>
        deleteMyRefundAccountByQuotationUser({
          variables: { input: { refundBankAccountId } }
        })
      );

      enqueueSnackbar('본인인증이 완료되었습니다.');
    } catch (error) {
      const errorMessage =
        (error as { message?: string }).message ?? '본인 인증 실패입니다.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      onClose?.();
    }
  };

  return (
    <RequestActionDialogBase
      open={open}
      onClose={onClose}
      iconSrc={Exclamation}
      title={'휴대폰 본인인증을 먼저 완료해주세요'}
      description={
        isTryingCreateRefundAccount
          ? '계좌를 등록하시기 전에 휴대폰 인증을 진행해주세요.'
          : '원활한 서비스 이용을 위하여 인증을 완료해 주세요.'
      }
      ButtonComponent={
        <CustomizedButton
          variant="contained"
          color="dark"
          size="large"
          onClick={handleVerificationClick}
        >
          인증하기
        </CustomizedButton>
      }
    />
  );
};

export default VerificationDialog;
